/**
 * apiV2 — axios instance wired to the Go API.
 *
 * Features
 * - Attaches Bearer token from authV2Store on every request.
 * - Unwraps the {success, data, error} envelope on every response.
 * - On 401 for non-auth endpoints: single-flight refresh rotation → retry → on
 *   failure clear session (ProtectedRouteV2 will redirect declaratively).
 * - Auth-endpoint 401s (wrong password, bad OTP, etc.) fall through to
 *   envelope-error extraction so ApiError reaches the login form.
 *
 * TODO (needs Go change): migrate refresh token to httpOnly cookie
 * TODO (needs Go change): multi-tab refresh grace window (concurrent tabs
 *      share the same refresh token; a grace period avoids thundering-herd
 *      on simultaneous token expiry across tabs)
 */
import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { ApiError, type LoginResult, type MeResult } from '@/features/auth/types';
import { STORAGE_KEYS, useAuthV2Store } from '@/features/auth/authV2Store';

// Validate at module load time in production.
if (import.meta.env.PROD && !import.meta.env.VITE_GO_API_BASE_URL) {
  throw new Error(
    '[apiV2] VITE_GO_API_BASE_URL must be set when building for production. ' +
    'Copy .env.example → .env.production and fill in the Go API base URL.',
  );
}

// Base URL from Vite env var, falling back to local Go server.
const BASE_URL =
  (import.meta.env.VITE_GO_API_BASE_URL as string | undefined) ??
  'http://localhost:4000/api/v1';

export const apiV2 = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

// ---- Request interceptor: attach Bearer token -------------------------------

apiV2.interceptors.request.use((config) => {
  const token = useAuthV2Store.getState().accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ---- Single-flight refresh state --------------------------------------------

let refreshPromise: Promise<string> | null = null;

async function doRefresh(): Promise<string> {
  const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
  if (!refreshToken) throw new Error('No refresh token available');

  // POST directly (no interceptor loop) using a bare axios call to avoid
  // the response interceptor unwrapping the refresh result incorrectly.
  const resp = await axios.post<{ success: boolean; data: LoginResult }>(
    `${BASE_URL}/auth/refresh`,
    { refresh_token: refreshToken },
    { headers: { 'Content-Type': 'application/json' } },
  );

  const result = resp.data.data;
  if (!result?.tokens) throw new Error('Refresh returned no tokens');

  const store = useAuthV2Store.getState();
  store.updateTokens({
    accessToken: result.tokens.access_token,
    refreshToken: result.tokens.refresh_token,
  });

  return result.tokens.access_token;
}

// ---- Response interceptor: unwrap envelope, handle 401 ---------------------

// Extend InternalAxiosRequestConfig to track retries.
interface RetryConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

apiV2.interceptors.response.use(
  (response) => {
    // Blob responses (PDF streams — features/exams hall-ticket downloads) are
    // never a {success, data} envelope; hand the Blob back untouched.
    if (response.config.responseType === 'blob') {
      return response.data as unknown as typeof response;
    }

    // Bodyless success (204 No Content, e.g. DELETE
    // /transport/assignments/:enrollmentID/:direction) — there is no envelope
    // to unwrap; axios gives data as '' for an empty body.
    if (response.status === 204 || response.data === '' || response.data == null) {
      return undefined as unknown as typeof response;
    }

    // Unwrap the success envelope. Return response.data.data (the payload).
    const envelope = response.data as {
      success: boolean;
      data?: unknown;
      meta?: unknown;
      message?: string;
    };

    if (!envelope.success) {
      throw new ApiError('REQUEST_FAILED', envelope.message ?? 'Request failed');
    }

    // Attach meta to the returned value for paginated responses.
    if (envelope.meta !== undefined) {
      return { data: envelope.data, meta: envelope.meta } as unknown as typeof response;
    }
    return envelope.data as unknown as typeof response;
  },

  async (error: AxiosError) => {
    const originalRequest = error.config as RetryConfig | undefined;

    // CRITICAL: do NOT intercept auth-endpoint 401s — those are intentional
    // (wrong password, bad OTP, expired selection token, etc.) and must reach
    // the calling code as ApiError so the login form can show an inline message.
    const isAuthEndpoint =
      originalRequest?.url?.includes('/auth/') ?? false;

    // A retried request that still 401s → session truly expired; log out and
    // let ProtectedRouteV2 redirect declaratively (no window.location.href).
    if (error.response?.status === 401 && originalRequest?._retry) {
      useAuthV2Store.getState().logout();
      return Promise.reject(
        new ApiError('SESSION_EXPIRED', 'Session expired, please log in again', 401),
      );
    }

    // Attempt single-flight token refresh for protected-endpoint 401s.
    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !isAuthEndpoint
    ) {
      const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
      if (!refreshToken) {
        // No refresh token → log out; ProtectedRouteV2 handles redirect.
        useAuthV2Store.getState().logout();
        return Promise.reject(new ApiError('UNAUTHENTICATED', 'Session expired', 401));
      }

      originalRequest._retry = true;

      try {
        // Single-flight: reuse an in-flight refresh if one is running.
        if (!refreshPromise) {
          refreshPromise = doRefresh().finally(() => {
            refreshPromise = null;
          });
        }
        const newToken = await refreshPromise;
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return apiV2(originalRequest);
      } catch {
        // Refresh failed → log out; ProtectedRouteV2 handles redirect.
        useAuthV2Store.getState().logout();
        return Promise.reject(
          new ApiError('SESSION_EXPIRED', 'Session expired, please log in again', 401),
        );
      }
    }

    // A blob-responseType request (PDF downloads) still receives its error
    // body as a Blob containing the JSON envelope — axios does not know to
    // parse it as JSON when the request was configured for binary. Read and
    // parse it here so ApiError still carries the real code/message instead
    // of a generic failure.
    if (originalRequest?.responseType === 'blob' && error.response?.data instanceof Blob) {
      try {
        const text = await error.response.data.text();
        const parsed = JSON.parse(text) as { success: false; error?: { code: string; message: string } };
        if (parsed.error) {
          return Promise.reject(
            new ApiError(parsed.error.code, parsed.error.message, error.response.status),
          );
        }
      } catch {
        // Fall through to the generic error below if the blob isn't JSON.
      }
      return Promise.reject(error);
    }

    // Extract error shape from the API envelope for all other errors.
    const body = error.response?.data as
      | { success: false; error?: { code: string; message: string } }
      | undefined;

    if (body?.error) {
      return Promise.reject(
        new ApiError(body.error.code, body.error.message, error.response?.status ?? 0),
      );
    }

    return Promise.reject(error);
  },
);

// ---- Typed convenience wrappers ---------------------------------------------

interface PaginatedResult<T> {
  data: T[];
  meta: { total: number; page: number; per_page: number };
}

export async function getPaginated<T>(
  path: string,
  params?: Record<string, string | number | undefined>,
): Promise<PaginatedResult<T>> {
  const result = await apiV2.get(path, { params });
  // Normalize: when the backend omits meta and returns a bare array,
  // synthesize a meta block so callers never get undefined.
  if (Array.isArray(result)) {
    const arr = result as T[];
    return { data: arr, meta: { total: arr.length, page: 1, per_page: arr.length } };
  }
  return result as unknown as PaginatedResult<T>;
}

export async function getOne<T>(path: string): Promise<T> {
  const result = await apiV2.get(path);
  return result as unknown as T;
}

export async function post<TBody, TResult>(path: string, body: TBody): Promise<TResult> {
  const result = await apiV2.post(path, body);
  return result as unknown as TResult;
}

export async function patch<TBody, TResult>(path: string, body: TBody): Promise<TResult> {
  const result = await apiV2.patch(path, body);
  return result as unknown as TResult;
}

export async function put<TBody, TResult>(path: string, body: TBody): Promise<TResult> {
  const result = await apiV2.put(path, body);
  return result as unknown as TResult;
}

export async function del(path: string): Promise<void> {
  await apiV2.delete(path);
}

/**
 * Authenticated GET → Blob, for on-demand PDF streams (features/exams hall
 * tickets: `GET .../hall-ticket.pdf`, D1 — rendered in-request, never
 * object-storage-backed). Goes through the same Bearer-token + 401-refresh
 * interceptors as every other apiV2 call; the response interceptor special-
 * cases `responseType: 'blob'` to skip the {success,data} envelope unwrap.
 */
export async function getBlob(path: string): Promise<Blob> {
  const result = await apiV2.get(path, { responseType: 'blob' });
  return result as unknown as Blob;
}

// ---- Auth-specific helpers --------------------------------------------------

export const authApi = {
  requestOTP: (phone: string) =>
    post<{ phone: string }, { request_id: string; expires_at: string; dev_code?: string }>(
      '/auth/otp/request',
      { phone },
    ),

  verifyOTP: (request_id: string, code: string, device_id?: string) =>
    post<{ request_id: string; code: string; device_id?: string }, LoginResult>(
      '/auth/otp/verify',
      { request_id, code, ...(device_id ? { device_id } : {}) },
    ),

  selectOrg: (selection_token: string, org_id: string, device_id?: string) =>
    post<{ selection_token: string; org_id: string; device_id?: string }, LoginResult>(
      '/auth/select-org',
      { selection_token, org_id, ...(device_id ? { device_id } : {}) },
    ),

  login: (username: string, password: string, org_code?: string, device_id?: string) =>
    post<
      { username: string; password: string; org_code?: string; device_id?: string },
      LoginResult
    >('/auth/login', {
      username,
      password,
      ...(org_code ? { org_code } : {}),
      ...(device_id ? { device_id } : {}),
    }),

  logout: (refresh_token: string) =>
    post<{ refresh_token: string }, Record<string, never>>('/auth/logout', { refresh_token }),

  me: () => getOne<MeResult>('/me'),
};
